import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ActionMeta } from 'react-select';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import { LookupService, SearchFilter } from '@nakedplatform/support';

import { NakedIcon } from 'theme/icons';
import { Container } from 'theme/layout';

import { debouncePromise } from './debounce-promise';
import {
  AutocompleteTheme,
  PreIconContainer,
  getAutocompleteStyles,
  getColorOverrideStyles,
  getColorOverrideTheme,
} from './styles';

const DEFAULT_KEYS = ['label'];
const ICON_SIZE = 12;
const DEFAULT_FUZZY_SEARCH_THRESHOLD = 0.6;

/**
 * Renders an autocomplete component.
 *
 * @param props - Autocomplete props.
 * @param ref - Autocomplete React Ref
 */
function AutocompleteComponent<T>(
  props: AutocompleteProps<T>,
  ref: React.ForwardedRef<AutocompleteRef>
) {
  const {
    defaultOption: option,
    noResultsText,
    onChange,
    onClear,
    onSelect,
    onNoResults,
    onResultsFound,
    options,
    placeholder,
    searchKeys = [],
    service,
    icon,
    isClearable,
    showDropdown,
    accentColor,
    accentTextColor,
    debounceEnabled = false,
    debounceDelay = 200,
    cacheOptions = true,
    allowFreeTextSubmission = false,
    fuzziness = DEFAULT_FUZZY_SEARCH_THRESHOLD,
    minCharLength = 1,
  } = props;
  const serviceSearchKeys = service?.searchKeys || [];

  const keys = DEFAULT_KEYS.concat(searchKeys, serviceSearchKeys);
  const search = useRef(new SearchFilter<AutocompleteOption>({ keys }));
  const [defaultOptions, setDefaultOptions] = useState(options || []);
  const [defaultOption, setDefaultOption] = useState(option);
  const [showMenu, setShowMenu] = useState(false);
  const [currentInputValue, setCurrentInputValue] = useState('');

  const itemSelected = useCallback(
    (item: AutocompleteOption, { action }: ActionMeta<AutocompleteOption>) => {
      if (action === 'select-option' && onSelect) {
        onSelect(item);
        setShowMenu(false);
      }

      if (action === 'clear' && onClear) {
        onClear();
      }

      if (onChange) {
        onChange(item);
      }

      if (defaultOption) {
        setDefaultOption(undefined);
      }
    },
    [onChange, onClear, onSelect, setDefaultOption, defaultOption]
  );

  /** Applies a search filter to the options to be displayed. */
  const applyFilter = useCallback(
    async (searchText: string) => {
      if (!options && !service) {
        throw new Error('You must supply at least one of "options" or "service".');
      }

      let collection = options || (await service.lookup(searchText));

      if (service) {
        collection = (collection as T[]).map(service.toAutocompleteOption);
      }

      search.current.setCollection(collection as AutocompleteOption[]);

      const results = await search.current.filter(searchText);

      setDefaultOptions(results || []);
      setShowMenu((results || []).length > 0);
      if (!results || results.length === 0) {
        onNoResults?.(searchText);
      } else {
        onResultsFound?.();
      }

      return results || [];
    },
    [options, service]
  );

  const debouncedApplyFilter = useMemo(() => {
    if (!debounceEnabled) {
      return applyFilter;
    }

    return debouncePromise(applyFilter, debounceDelay);
  }, [debounceEnabled, debounceDelay, applyFilter]);

  /**
   * Determines if a new option should be created.
   * This controls when the "create option" appears in the dropdown.
   */
  const isValidNewOption = useCallback(
    (inputValue: string) => {
      // Don't allow creation if input is less than the minimum number of characters
      if (inputValue.length < minCharLength) {
        return false;
      }
      return true;
    },
    [minCharLength]
  );

  /**
   * Sets a custom option when the user types in a value that is not in the list of options.
   * This is used to allow the user to submit a free text value if no options are found.
   */
  const setCustomOption = useCallback(() => {
    // Use the same validation logic as isValidNewOption
    if (!isValidNewOption(currentInputValue)) {
      return;
    }

    if (currentInputValue.trim() && (!defaultOptions || defaultOptions.length === 0)) {
      const newOption: AutocompleteOption = {
        label: currentInputValue.trim(),
        value: currentInputValue.trim(),
      };

      itemSelected(newOption, { action: 'select-option' });
      setDefaultOption(newOption);
    }
  }, [currentInputValue, defaultOptions, itemSelected, isValidNewOption]);

  /**
   * Handles the blur event for the autocomplete component.
   * This is used to close the dropdown menu when the user clicks outside of the component.
   */
  const handleBlur = useCallback(() => {
    setShowMenu(false);
    setCustomOption();
  }, [setCustomOption]);

  /**
   * Handles the key down event for the autocomplete component.
   * This is used to allow the user to submit a free text value if no options are found when the Enter key is pressed.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        setCustomOption();
        event.preventDefault();
      }
    },
    [setCustomOption]
  );

  /**
   * Handles the input change event for the autocomplete component.
   * This is used to close the dropdown menu when the user deletes all the text in the input field.
   * It also sets the current input value to the new value.
   */
  const handleInputChange = useCallback((newValue: string) => {
    if (newValue.length < 1) {
      setShowMenu(false);
    }

    setCurrentInputValue(newValue);
  }, []);

  /**
   * Formats the suggestion label for the autocomplete component.
   * This is used to display the suggestion label in the dropdown menu.
   */
  const formatSuggestionLabel = useCallback((inputValue: string) => {
    return (
      <span>
        Use{' '}
        <b>
          <em>{inputValue}</em>
        </b>
      </span>
    );
  }, []);

  const { styledComponents, autoCompleteStyles: baseAutoCompleteStyles } = getAutocompleteStyles(
    icon,
    showDropdown
  );
  const autoCompleteStylesWithColorOverrides = getColorOverrideStyles(
    baseAutoCompleteStyles,
    accentColor,
    accentTextColor
  );
  const autoCompleteThemeWithColorOverrides = getColorOverrideTheme(AutocompleteTheme, accentColor);
  const autoCompleteTheme = accentColor ? autoCompleteThemeWithColorOverrides : AutocompleteTheme;
  const autoCompleteStyles = accentColor
    ? autoCompleteStylesWithColorOverrides
    : baseAutoCompleteStyles;

  useEffect(() => {
    setDefaultOptions(options || []);
  }, [options]);

  useEffect(() => {
    search.current.setThreshold(fuzziness);
  }, [fuzziness]);

  return (
    <Container position="relative">
      {icon && (
        <PreIconContainer>
          <NakedIcon name={icon} size={ICON_SIZE} />
        </PreIconContainer>
      )}
      {allowFreeTextSubmission ? (
        <AsyncCreatableSelect<AutocompleteOption, false>
          isClearable={isClearable}
          cacheOptions={false}
          loadOptions={debouncedApplyFilter}
          components={styledComponents}
          styles={autoCompleteStyles}
          theme={autoCompleteTheme}
          defaultValue={defaultOption}
          placeholder={placeholder}
          onChange={itemSelected}
          value={defaultOption}
          noOptionsMessage={noResultsText}
          formatCreateLabel={formatSuggestionLabel}
          isValidNewOption={isValidNewOption}
          blurInputOnSelect={true}
          onBlur={handleBlur}
          menuIsOpen={showMenu}
          onKeyDown={handleKeyDown}
          onInputChange={handleInputChange}
        />
      ) : (
        <AsyncSelect<AutocompleteOption, false>
          isClearable={isClearable}
          cacheOptions={cacheOptions}
          ref={ref as React.Ref<AsyncSelect<AutocompleteOption, false>>}
          components={styledComponents}
          defaultOptions={defaultOptions}
          loadOptions={debouncedApplyFilter}
          onChange={itemSelected}
          theme={autoCompleteTheme}
          styles={autoCompleteStyles}
          defaultValue={defaultOption}
          placeholder={placeholder}
          noOptionsMessage={noResultsText}
        />
      )}
    </Container>
  );
}
// Use React.forwardRef with proper typing for React 16 compatibility
export const Autocomplete = React.forwardRef(AutocompleteComponent);
export interface AutocompleteProps<T = unknown> {
  /** Value shown when the Autocomplete is initially shown. */
  defaultOption?: AutocompleteOption;
  /** A function that loads a list of options. */
  loadOptions?: (searchText: string) => Promise<AutocompleteOption[]>;
  /** Text to display when no results found.  */
  noResultsText?: NoResultsCallback;
  /** Emits an `onChange` event whenever a selection has changed. */
  onChange?(option?: AutocompleteOption): void;
  /** Emits the `onClear` event when field is clear */
  onClear?(): void;
  /** Emits an `onSelect` event when an option is selected. */
  onSelect?(option: AutocompleteOption): void;
  /** Emits an `onNoResults` event when no results are found. */
  onNoResults?(searchText: string): void;
  /** Emits an `onResultsFound` event when results are found. */
  onResultsFound?(): void;
  /** **Static** list of options to display (no remote call). */
  options?: AutocompleteOption[];
  /** The placeholder text to display when no option is selected. */
  placeholder?: string;
  /** A list of keys to search by (contained within an option). */
  searchKeys?: string[];
  /** A service that performs dynamic data lookups. */
  service?: AutocompleteLookupService<T>;
  /** The icon to use with the field */
  icon?: string;
  /** Whether the field implements a clearing button*/
  isClearable?: boolean;
  /** Whether the field shows the dropdown button*/
  showDropdown?: boolean;
  /** Color with which to override the accent color */
  accentColor?: string;
  /** Color with which to override text that appears on the accent color */
  accentTextColor?: string;
  /** Whether to debounce the async lookup calls. Defaults to false. */
  debounceEnabled?: boolean;
  /** Debounce delay in ms. Defaults to 200 if not specified. */
  debounceDelay?: number;
  /** Whether to cache the options. Defaults to true. */
  cacheOptions?: boolean;
  /** Whether the field allows free text input. Defaults to false. */
  allowFreeTextSubmission?: boolean;
  /** Fuzziness level for the search filter. Defaults to 0.6. */
  fuzziness?: number;
  /** Minimum character length required for free text submission. Defaults to 1. */
  minCharLength?: number;
}
export type NoResultsCallback = (search: SearchTerm) => string;
export interface SearchTerm {
  /** Current text value entered */
  inputValue: string;
}
export interface AutocompleteOption {
  /** The string that is displayed on the dropdown option. */
  label: string;
  /** The string that represents the chosen option. */
  value: string | number;
}
export type AutocompleteRef = AsyncSelect<AutocompleteOption>;
export interface AutocompleteLookupService<T> extends LookupService<T> {
  /**
   * Converts service-specific data to a compatible autocomplete option format.
   *
   * Note: A static method would be better, but is not supported on interfaces by TS at the moment.
   *
   * @see {@link https://github.com/microsoft/TypeScript/issues/34516} for abstract classes.
   * @see {@link https://github.com/microsoft/TypeScript/issues/33892} for interface declarations.
   *
   * @param serviceData - Service-specific data format.
   */
  toAutocompleteOption(serviceData: unknown): AutocompleteOption;
}
